import { useState, useContext, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GlobalContext } from "../../../context/Context";
import { TableCard } from "../../../component/table/table-layout";
import {
  FeildWraper,
  FormArea,
  FormLabel,
  FeildError,
} from "../../../component/form/form-layout";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import Select from "react-select";
import { Loader2 } from "lucide-react";
import { Alert, Snackbar } from "@mui/material";

const LoginPassword = ({ Style }) => {
  const { state, dispatch } = useContext(GlobalContext);
  const apiCall = useApiCall();

  const [alertData, setAlertData] = useState({ isShow: false });

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message });
  };

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message });
  };

  const handleClose = () => {
    setAlertData({ isShow: false });
  };

  const [activeTab, setActiveTab] = useState("personal");

  // location lists + loading
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [loadingCountry, setLoadingCountry] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  // read user data from context or localStorage
  const userData = state?.user || JSON.parse(localStorage.getItem("user") || "{}");

  // ---------- Formik: Password ----------
  const passwordValidation = Yup.object({
    current: Yup.string().required("Current password is required"),
    newPass: Yup.string()
      .min(8, "New password must be at least 8 characters")
      .required("New password is required"),
    confirm: Yup.string()
      .oneOf([Yup.ref("newPass"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const passwordFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      current: "",
      newPass: "",
      confirm: "",
      logout_all: false,
    },
    validationSchema: passwordValidation,
    onSubmit: async (values, { setSubmitting, setFieldError, resetForm }) => {
      try {
        const payload = {
          current_password: values.current,
          new_password: values.newPass,
          new_password_confirmation: values.newPass,
          logout_all: values.logout_all,
        };


        const res = await apiCall({
          method: "post",
          url: "update-my-password",
          data: payload,
        });

        onSuccess(res?.message || `Password updated successfully`);
        resetForm();
      } catch (err) {
        console.error("Change password error:", err);
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.respMsg ||
          "Failed to change password";
        // If backend provides field-specific error, set it
        if (err?.response?.data?.field === "current_password") {
          setFieldError("current", msg);
        } else {
          setFieldError("general", msg);
        }

        onError(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Helper initials
  const getInitials = (name = "") => {
    if (!name) return "U";
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // select styles (same as you used)
  const selectStyles = {
    control: (base, s) => ({
      ...base,
      minHeight: "35px",
      height: "auto",
      borderRadius: "5px",
      border: "1px solid #DFDFDF",
      backgroundColor: s.isDisabled ? "#f0f0f0" : "#FFF",
      boxShadow: "none",
      "&:hover": { borderColor: "#DFDFDF" },
      fontSize: "13px",
      lineHeight: "1.2",
      fontWeight: 500,
      color: "#1E1E1E",
      padding: "0 14px",
      cursor: s.isDisabled ? "not-allowed" : "normal",
    }),
    valueContainer: (base) => ({ ...base, height: "auto", padding: "0 0px 0" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "14px", fontWeight: 500 }),
    singleValue: (base) => ({ ...base, fontSize: "14px", fontWeight: 500 }),
    placeholder: (base) => ({ ...base, fontSize: "14px", fontWeight: 500, color: "#717680" }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    menu: (base) => ({ ...base, marginTop: "2px", borderRadius: "5px", border: "1px solid #DFDFDF", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }),
    option: (base, s) => ({
      ...base,
      fontSize: "14px",
      fontWeight: 500,
      color: s.isSelected ? "#FFF" : "#1E1E1E",
      backgroundColor: s.isSelected ? "#3b82f6" : s.isFocused ? "#F3F4F6" : "#FFF",
      padding: "8px 14px",
      cursor: "pointer",
    }),
    indicatorContainer: (base) => ({ ...base, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 0 }),
  };







  return (
    <>
      <TableCard className={'overflow-hidden'}>
        <div className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">

          {/* Tab content */}
          <form onSubmit={passwordFormik.handleSubmit} className="p-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-6">Change Password</h3>

            <FormArea columns={3}>
              <FeildWraper>
                <FormLabel>Current Password</FormLabel>
                <input
                  type="password"
                  name="current"
                  value={passwordFormik.values.current}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  placeholder="Enter current password"

                />
                {passwordFormik.touched.current && passwordFormik.errors.current && (
                  <FeildError>{passwordFormik.errors.current}</FeildError>
                )}
              </FeildWraper>

              <FeildWraper>
                <FormLabel>New Password</FormLabel>
                <input
                  type="password"
                  name="newPass"
                  value={passwordFormik.values.newPass}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  placeholder="Enter new password"

                />
                {passwordFormik.touched.newPass && passwordFormik.errors.newPass && (
                  <FeildError>{passwordFormik.errors.newPass}</FeildError>
                )}
              </FeildWraper>

              <FeildWraper width="100%">
                <FormLabel>Confirm Password</FormLabel>
                <input
                  type="password"
                  name="confirm"
                  value={passwordFormik.values.confirm}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  placeholder="Confirm new password"

                />
                {passwordFormik.touched.confirm && passwordFormik.errors.confirm && (
                  <FeildError>{passwordFormik.errors.confirm}</FeildError>
                )}
              </FeildWraper>


            </FormArea>

            <label className="flex items-center space-x-2 mt-4">
              <input
                type="checkbox"
                name="logout_all"
                onChange={(e) => passwordFormik.setFieldValue('logout_all', e.target.checked)}
              />
              <span>Logout from all other devices</span>
            </label>


            <div className="flex justify-end mt-6">
              <button
                type="submit"
                disabled={passwordFormik.isSubmitting}
                className="bg-gray-800 hover:bg-gray-900 text-white px-5 py-2.5 rounded-lg transition-all font-medium"
              >
                {passwordFormik.isSubmitting ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </div>
      </TableCard>



      <Snackbar
        open={alertData?.isShow}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LoginPassword;
